import React from "react";
import {
  Stack,
  Typography,
  Paper,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Divider,
  Box,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  SyncAlt,
  CloudDownload,
  Schedule,
  Storage,
  PlayArrow,
  AutoFixHigh,
} from "@mui/icons-material";
import { useOdooMigrationPermissions } from "../hooks/useOdooMigrationPermissions";
import { useOdooProductionSyncHandlers } from "../hooks/useOdooProductionSyncHandlers";
import { OdooProductionMigrationButton } from "../components/odoo-production-migration/OdooProductionMigrationButton";
import { OdooProductionMigrationMonitor } from "../components/odoo-production-migration/OdooProductionMigrationMonitor";
import { OdooProductionMigrationLogs } from "../components/odoo-production-migration/OdooProductionMigrationLogs";
import { DistribucionPostSyncMonitor } from "../components/Distribution/DistribucionPostSyncMonitor";

export function OdooProductionSyncPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { canView, canExecuteManual, canViewLogs, isLoading } =
    useOdooMigrationPermissions();

  // Handlers + estado encapsulados en hook (P7 split)
  const {
    snackbar,
    migrationStatus,
    monitorKey,
    isRunningPostMigration,
    isRunningDistribution,
    isRunningClassification,
    handleMigrationStarted,
    handleMigrationError,
    handleStatusChange,
    handleCloseSnackbar,
    handleRunDistributionOnly,
    handleRunPostMigrationOnly,
    handleRunOpenAiClassification,
  } = useOdooProductionSyncHandlers();

  if (isLoading) {
    return (
      <Stack spacing={3} sx={{ p: isDesktop ? 3 : 2 }}>
        <Typography variant="h4">Sincronizacion de Produccion Odoo</Typography>
        <Alert severity="info">Verificando permisos...</Alert>
      </Stack>
    );
  }

  if (!canView) {
    return (
      <Stack spacing={3} sx={{ p: isDesktop ? 3 : 2 }}>
        <Typography variant="h4">Sincronizacion de Produccion Odoo</Typography>
        <Alert severity="warning">
          No tiene permisos para acceder a esta funcionalidad. Contacte al
          administrador para obtener permisos de
          "/odoo-erp/migration-produccion/ver".
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: isDesktop ? 3 : 2 }}>
      {/* Titulo y descripcion */}
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SyncAlt color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Sincronizacion de Produccion Odoo
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Copia de solo lectura de la base de datos Odoo de produccion hacia una
          copia local. Se ejecuta automaticamente cada hora. No
          modifica el servidor de produccion.
        </Typography>
      </Stack>

      {/* Resumen del proceso */}
      <Paper
        sx={{
          p: 2,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.primary"
          >
            Como funciona
          </Typography>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CloudDownload fontSize="small" color="primary" />
              <Typography variant="body2">
                <strong>Origen:</strong> VillApps (servidor de produccion Odoo)
                — solo lectura via pg_dump
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Storage fontSize="small" color="primary" />
              <Typography variant="body2">
                <strong>Destino:</strong> odoo_18 (copia local) — se reemplaza
                completamente con datos frescos
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Schedule fontSize="small" color="primary" />
              <Typography variant="body2">
                <strong>Programacion:</strong> Automatica cada hora
              </Typography>
            </Stack>
          </Stack>
          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
            Esta operacion es de <strong>solo lectura</strong> en el servidor de
            produccion. Unicamente ejecuta pg_dump (respaldo) y descarga los
            datos. La restauracion ocurre exclusivamente en la base de datos
            local.
          </Alert>
        </Stack>
      </Paper>

      {/* Boton de sincronizacion manual */}
      {canExecuteManual && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "warning.light",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" fontWeight="bold">
                Sincronizacion Manual
              </Typography>
              <Chip
                label="Bajo demanda"
                size="small"
                color="warning"
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Ejecutar una sincronizacion fuera del horario programado. Util
              cuando se necesitan datos actualizados de produccion
              inmediatamente.
            </Typography>

            <Box>
              <OdooProductionMigrationButton
                onMigrationStarted={handleMigrationStarted}
                onMigrationError={handleMigrationError}
              />
            </Box>

            <Alert
              severity="warning"
              variant="outlined"
              sx={{ fontSize: "0.875rem" }}
            >
              La sincronizacion puede tomar entre 5 y 30 minutos dependiendo del
              tamano de la base de datos y la velocidad de red.
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* Boton de post-migracion standalone */}
      {canExecuteManual && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "info.light",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" fontWeight="bold">
                Re-ejecutar Post-Migracion
              </Typography>
              <Chip
                label="Forecast + Modelos"
                size="small"
                color="info"
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Ejecuta unicamente los procesos posteriores a la migracion
              (forecast de compras, modelos de inventario, sincronizacion de
              tiendas) sin volver a descargar la base de datos de produccion.
              Util para re-procesar cuando ya se tienen datos frescos en
              odoo_18. <strong>No incluye clasificacion OpenAI</strong> —
              dispararla por separado en la seccion de abajo.
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="info"
                size="small"
                onClick={handleRunPostMigrationOnly}
                disabled={
                  isRunningPostMigration ||
                  (migrationStatus?.isRunning ?? false)
                }
                startIcon={
                  isRunningPostMigration ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlayArrow />
                  )
                }
                sx={{ fontWeight: "bold", px: 3, py: 1 }}
              >
                {isRunningPostMigration
                  ? "Ejecutando Post-Migracion..."
                  : "Ejecutar Solo Post-Migracion"}
              </Button>
            </Box>

            <Alert
              severity="info"
              variant="outlined"
              sx={{ fontSize: "0.875rem" }}
            >
              Este proceso toma entre 1 y 5 minutos. NO descarga datos del
              servidor de produccion ni ejecuta clasificacion OpenAI.
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* Boton dedicado para clasificacion OpenAI */}
      {canExecuteManual && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "secondary.light",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" fontWeight="bold">
                Clasificacion OpenAI
              </Typography>
              <Chip
                label="Bajo demanda"
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Clasifica con OpenAI (gpt + web_search) los productos del
              catalogo que aun no tienen categoria. Aislado del flujo de
              sincronizacion para controlar costo y tiempo. Respeta categorias
              marcadas como manuales — nunca las sobrescribe.
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleRunOpenAiClassification}
                disabled={
                  isRunningClassification ||
                  (migrationStatus?.isRunning ?? false)
                }
                startIcon={
                  isRunningClassification ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AutoFixHigh />
                  )
                }
                sx={{ fontWeight: "bold", px: 3, py: 1 }}
              >
                {isRunningClassification
                  ? "Clasificando con OpenAI..."
                  : "Ejecutar Clasificacion OpenAI"}
              </Button>
            </Box>

            <Alert
              severity="warning"
              variant="outlined"
              sx={{ fontSize: "0.875rem" }}
            >
              Solo procesa productos sin clasificar. Tiempo estimado: minutos
              (depende del numero de productos pendientes y rate limits del
              API). Llama a <code>POST /forecasting/openai-classify</code>.
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* Solo recálculo de distribución (atajo dev §7 plan distribución) */}
      {canExecuteManual && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "success.light",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" fontWeight="bold">
                Solo Recalcular Distribución
              </Typography>
              <Chip
                label="Pipeline §7"
                size="small"
                color="success"
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Dispara únicamente el orquestador post-sync de distribución
              (refresca <code>monthly_product_store_sales</code>, stockouts,
              forecast por tienda, ROP, <code>distribution_recommendation</code>{" "}
              y criticality_score). NO re-corre la migración Odoo ni el
              forecast de compras. Útil en dev para iterar el cálculo de
              recomendaciones sin esperar el flujo completo.
            </Typography>

            <Box>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleRunDistributionOnly}
                disabled={
                  isRunningDistribution ||
                  isRunningPostMigration ||
                  (migrationStatus?.isRunning ?? false)
                }
                startIcon={
                  isRunningDistribution ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PlayArrow />
                  )
                }
                sx={{ fontWeight: "bold", px: 3, py: 1 }}
              >
                {isRunningDistribution
                  ? "Ejecutando recálculo..."
                  : "Ejecutar recálculo de distribución"}
              </Button>
            </Box>

            <Alert
              severity="info"
              variant="outlined"
              sx={{ fontSize: "0.875rem" }}
            >
              Llama directo a <code>POST /api/admin/post-sync/trigger</code>.
              Equivalente a lo que dispara automáticamente la migración al
              terminar (fire-and-forget en{" "}
              <code>OdooSyncLogService.logComplete</code>).
            </Alert>
          </Stack>
        </Paper>
      )}

      {/* Monitor del orquestador post-sync §7 (timeline último run + histórico) */}
      {canViewLogs && <DistribucionPostSyncMonitor />}

      <Divider />

      {/* Monitor de estado */}
      <Stack spacing={3}>
        <Typography variant="h5" fontWeight="bold">
          Estado Actual
        </Typography>

        <OdooProductionMigrationMonitor
          key={monitorKey}
          onStatusChange={handleStatusChange}
          refreshInterval={2000}
        />
      </Stack>

      <Divider />

      {/* Historial */}
      {canViewLogs && (
        <Stack spacing={3}>
          <Typography variant="h5" fontWeight="bold">
            Historial de Sincronizaciones
          </Typography>

          <OdooProductionMigrationLogs defaultLimit={15} autoRefresh={false} />
        </Stack>
      )}

      {/* Informacion tecnica */}
      <Paper sx={{ p: 2, backgroundColor: "action.hover" }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Detalles tecnicos
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Conexion: SSH con clave privada al servidor Bitnami de produccion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Respaldo: pg_dump con compresion gzip en servidor remoto
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Transferencia: SCP con compresion hacia directorio temporal local
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Restauracion: psql directo sobre BD local odoo_18 (drop + create +
            restore)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Limpieza: Eliminacion automatica de archivos temporales locales y
            remotos
          </Typography>
        </Stack>
      </Paper>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
